<template>
  <div
    :id="`${step?.step_type || 'unknown'}_step_header`"
    class="step_separator p-2.5 flex flex-row gap-4 items-center"
  >
    <span class="ml-2 font-semibold text-sm">{{ step?.position || "?" }}.</span>
    <div class="flex p-2 rounded-xl bg_white_8">
      <ThemeImage
        :icon="getIcon"
        :alt="step?.step_type || 'step'"
        class="size-6"
      />
    </div>

    <div class="flex flex-row gap-2 justify-between w-full">
      <div class="flex flex-col gap-2 justify-center">
        <span class="text-[15px] font-semibold">
          {{ stepTypeText }} - Day {{ step?.relative_day || 1 }}
        </span>
        <span class="text-[13px] secondary_color">
          <span v-if="step?.timing_type === 'IMMEDIATE'">
            {{
              step?.position === 1
                ? `Scheduled immediately after the person is added to ${featureNames.SEQUENCES}`
                : "Scheduled immediately after the previous step"
            }}
          </span>
          <span v-else>
            Step {{ step?.position || "0" }} - Schedule in
            {{ step?.delay_time || 0 }}
            {{
              step?.delay_time === 1
                ? step?.delay_time_unit?.slice(0, -1).toLowerCase() || "day"
                : step?.delay_time_unit?.toLowerCase() || "days"
            }}
            if no reply received
          </span>
          <template
            v-if="step?.automation_type === 'TASK' && step?.task_priority"
          >
            - {{ step.task_priority }}
          </template>
        </span>
      </div>
      <div class="flex flex-row items-center">
        <StepMetrics :metrics="stepMetrics" :step="step" />
        <CustomDropdown
          maxHeight="300px"
          gap="10px"
          position="end"
          v-if="canEdit"
        >
          <template #trigger>
            <div class="quick_action_item mt-1">
              <ThemeImage
                :icon="imageNames.MORE_VERTICAL"
                alt="three_dots"
                class="size-5 cursor-pointer opacity-60"
              />
            </div>
          </template>
          <template #default>
            <div class="flex flex-col gap-1">
              <div
                v-for="option in stepActions"
                :key="option.id"
                class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
                @click="option.action()"
              >
                <ThemeImage
                  :icon="option.icon"
                  alt="icon"
                  :class="option.iconClass"
                />
                <div class="text-sm">{{ option.name }}</div>
              </div>
            </div>
          </template>
        </CustomDropdown>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import StepMetrics from "@/src/views/sequence/step/StepMetrics.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import { stepOptions } from "@/src/constants/sequenceStepOptions.js";
import { sequenceAutomationTypes } from "@/src/constants/sequenceConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  step: { type: Object, required: true },
  canEdit: { type: Boolean, required: true },
  stepMetrics: { type: Object, required: true },
  stepTypeText: { type: String, required: true },
  totalSteps: { type: Number, required: false, default: 1 },
});

const emit = defineEmits([
  "edit",
  "delete",
  "move_up",
  "move_down",
  "add_above",
  "add_below",
]);

const assignedTeamMemberName = ref("");

const fetchTeamMembers = async () => {
  try {
    const response = await DashboardService.fetchUsers("active");
    if (response?.success) {
      if (props.step?.assigned_to_id) {
        const assignedMember = response.users.find(
          (user) => user.id === props.step.assigned_to_id,
        );
        if (assignedMember) {
          assignedTeamMemberName.value = assignedMember.name;
        }
      }
    }
  } catch (error) {
    console.error("Error fetching team members:", error);
  }
};

onMounted(() => {
  fetchTeamMembers();
});

const stepActions = computed(() => {
  const actions = [
    {
      id: 1,
      name: "Edit Step",
      action: () => emit("edit"),
      icon: imageNames.EDIT,
      iconClass: "size-5",
    },
  ];
  if (props.step?.position > 1) {
    actions.push({
      id: 2,
      name: "Move Up",
      action: () => emit("move_up"),
      icon: imageNames.BACK_ARROW_DEFAULT_GREY,
      iconClass: "size-5 rotate-90",
    });
  }
  if (props.step?.position < props.totalSteps) {
    actions.push({
      id: 3,
      name: "Move Down",
      action: () => emit("move_down"),
      icon: imageNames.BACK_ARROW_DEFAULT_GREY,
      iconClass: "size-5 -rotate-90",
    });
  }
  actions.push(
    {
      id: 4,
      name: "Add Step Above",
      action: () => emit("add_above"),
      icon: imageNames.ADD_STEP_GREY,
      iconClass: "size-5",
    },
    {
      id: 5,
      name: "Add Step Below",
      action: () => emit("add_below"),
      icon: imageNames.ADD_STEP_GREY,
      iconClass: "size-5 rotate-180",
    },
    {
      id: 6,
      name: "Delete Step",
      action: () => emit("delete"),
      icon: imageNames.DELETE_GREY,
      iconClass: "size-5",
    },
  );

  return actions;
});

const getIcon = computed(() => {
  const { automation_type, step_type } = props.step || {};

  const category =
    automation_type === sequenceAutomationTypes.AI
      ? stepOptions?.ai
      : automation_type === sequenceAutomationTypes.AUTOMATED
        ? stepOptions?.automated
        : stepOptions?.manual;

  const option = category?.options?.find(
    (opt) =>
      opt?.stepType === step_type && opt?.automationType === automation_type,
  );

  return option?.icon || "";
});
</script>
